import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import { DatePickerProps } from '@verantos-dev/shared-components';
import { IndexDateEvent, IndexDateTypeLegacy } from '@verantos-dev/types-cohorts';
import { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import { useCohortEditable } from './useCohortEditable';
import {
  clearModal,
  setActiveCohortBuilderItemIndexDateType,
  setActiveCohortBuilderItemIndexDateValue,
  setIndexDate,
} from '../cohortBuilderSlice';
import {
  selectActiveCohortBuilderItemId,
  selectActiveCohortBuilderItemIndexDateType,
  selectActiveCohortBuilderItemIndexDateValue,
  selectActiveCohortBuilderItemInputFieldConfigMap,
  selectActiveCohortBuilderItemIndexDateRange,
  selectActiveCohortBuilderItemIndexDateEvent,
} from '../selectors/cohortBuilderEditModalSelectors';
import { selectCohortDefinition } from '../selectors/cohortDefinitionSelectors';
import { getFullCohortDslForIndexDate } from '../utils/dsl-generators/getFullCohortDslForIndexDate';

dayjs.extend(utc);

export const useIndexDateEdit = (minDate: Dayjs, maxDate: Dayjs) => {
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const { isEditable } = useCohortEditable();

  const currentCohortDefinition = useAppSelector(selectCohortDefinition);

  // select the index date and modal from the store
  const indexDateId = useAppSelector(selectActiveCohortBuilderItemId) ?? uuidv4(); // if no id, then we are creating a new Derived event
  const indexDateType = useAppSelector(selectActiveCohortBuilderItemIndexDateType) ?? '';
  const indexDateValue = useAppSelector(selectActiveCohortBuilderItemIndexDateValue);
  const indexDateEvent = useAppSelector(selectActiveCohortBuilderItemIndexDateEvent);
  const indexDateRangeValue = useAppSelector(selectActiveCohortBuilderItemIndexDateRange);
  const inputFieldConfigMap =
    useAppSelector(selectActiveCohortBuilderItemInputFieldConfigMap) ?? {};

  const [datePickerErrorMsg, setDatePickerErrorMsg] = useState<string | null>(null);
  const [hasDateBoundsError, setHasDateBoundsError] = useState<boolean>(false);
  const [indexDateValueAsDate, setIndexDateValueAsDate] = useState<Dayjs | null>(
    indexDateType === IndexDateTypeLegacy.CALENDAR && indexDateValue
      ? dayjs.utc(new Date(indexDateValue))
      : null
  );

  const [indexDateRangeStartValue, setIndexDateRangeStartValue] = useState<Dayjs | null>(
    indexDateRangeValue?.startDate ? dayjs.utc(new Date(indexDateRangeValue?.startDate)) : null
  );
  const [indexDateRangeEndValue, setIndexDateRangeEndValue] = useState<Dayjs | null>(
    indexDateRangeValue?.endDate ? dayjs.utc(new Date(indexDateRangeValue?.endDate)) : null
  );
  const [isDateRangeEnabled, setIsDateRangeEnabled] = useState<boolean>(
    indexDateRangeValue && indexDateRangeValue?.startDate && indexDateRangeValue?.endDate
      ? true
      : false
  );

  // Check if index date value is valid
  const indexDateValueIsValid = useMemo(() => {
    if (indexDateType === IndexDateTypeLegacy.CALENDAR) {
      return indexDateValueAsDate && indexDateValueAsDate.isValid();
    }
    if (indexDateType === IndexDateTypeLegacy.EVENT) {
      if (indexDateValue && indexDateValue.length > 0) {
        return true;
      } else if (indexDateEvent) {
        const isEventNameValid = indexDateEvent.eventName.value;
        const isEventDateAttributeValid = indexDateEvent.eventDateAttribute.value;
        const isOccurrenceValid = indexDateEvent.occurrence.value;
        const isPositionValid =
          isOccurrenceValid === 'NTH'
            ? indexDateEvent.position?.value && Number(indexDateEvent.position?.value) > 0
            : true;
        if (isEventNameValid && isEventDateAttributeValid && isOccurrenceValid && isPositionValid) {
          return true;
        }
      }
      return false;
    }
    return false;
  }, [indexDateType, indexDateValueAsDate, indexDateValue, indexDateEvent]);

  // Check if save is allowed
  const isSaveAllowed = useMemo(() => {
    return Boolean(
      indexDateId &&
        indexDateType &&
        indexDateValueIsValid &&
        !datePickerErrorMsg &&
        !hasDateBoundsError &&
        (isDateRangeEnabled ? indexDateRangeStartValue && indexDateRangeEndValue : true)
    );
  }, [
    indexDateId,
    indexDateType,
    indexDateValueIsValid,
    datePickerErrorMsg,
    hasDateBoundsError,
    isDateRangeEnabled,
    indexDateRangeStartValue,
    indexDateRangeEndValue,
  ]);

  /** Click Handlers */
  // select the index type within the modal
  const onSelectTypeChange = (id: string, value: string) => {
    if ((value as IndexDateTypeLegacy) !== indexDateType) {
      setIndexDateValueAsDate(null);
    }
    if (value) {
      dispatch(
        setActiveCohortBuilderItemIndexDateType({
          id: indexDateId,
          type: value as IndexDateTypeLegacy,
        })
      );
    }

    if (value !== indexDateType) {
      // clear the date value if the type changes
      setIndexDateRangeStartValue(null);
      setIndexDateRangeEndValue(null);
      setIsDateRangeEnabled(false);
      setIndexDateValueAsDate(null);
      setHasDateBoundsError(false);
    }
  };

  // select the index date within the modal
  const onDatePickerChange = (value: Dayjs | null) => {
    setIndexDateValueAsDate(value);
    // Clear error when date changes
    if (value && value.isValid()) {
      setDatePickerErrorMsg(null);
    }
    // only dispatch value update for calendar handler if the selected type is calendar
    // add the test for year < 3000 to prevent invalid dates from being saved
    if (
      indexDateType === IndexDateTypeLegacy.CALENDAR &&
      value &&
      value.isValid() &&
      value.year() < 3000
    ) {
      const calendarDate = dayjs.utc(value); // Convert to Day.js instance
      dispatch(
        setActiveCohortBuilderItemIndexDateValue({
          id: indexDateId,
          type: indexDateType,
          indexDateValue: dayjs.utc(calendarDate).format('YYYY-MM-DD'),
          indexDateEvent: undefined,
        })
      );
    }
  };

  const onDatePickerError = (_id: string, reason: string | null) => {
    if (reason === null) {
      setDatePickerErrorMsg(null);
      return;
    }
    switch (reason) {
      case 'invalidDate':
        setDatePickerErrorMsg(
          intl.formatMessage({
            id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateInvalidError',
          })
        );
        break;
      case 'minDate':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateMinDateError' },
            { minDate: minDate.format('MM/DD/YYYY') }
          )
        );
        break;
      case 'maxDate':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateMaxDateError' },
            { maxDate: maxDate.format('MM/DD/YYYY') }
          )
        );
        break;
      case 'disableFuture':
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateFutureError' },
            { today: dayjs.utc().format('MM/DD/YYYY') }
          )
        );
        break;
      default:
        setDatePickerErrorMsg(
          intl.formatMessage(
            { id: 'projects.cohortBuilderPage.setIndexDateDialog.calDateUnknownError' },
            { error: reason }
          )
        );
    }
  };

  const onDateRangeError = (_id: string, detail: string | null) => {
    setHasDateBoundsError(detail !== null);
  };

  const onEventChange = (value: string) => {
    if (indexDateType === IndexDateTypeLegacy.EVENT) {
      if (value) {
        let updatedIndexDateEvent = undefined;
        if (indexDateEvent) {
          updatedIndexDateEvent = {
            ...indexDateEvent,
            eventName: {
              ...indexDateEvent.eventName,
              value: value,
            },
            eventDateAttribute: {
              ...indexDateEvent.eventDateAttribute,
              value: '',
            },
            occurrence: {
              ...indexDateEvent.occurrence,
              value: 'FIRST',
            },
            position: {
              ...indexDateEvent.position,
              value: '',
            },
          };
        }
        dispatch(
          setActiveCohortBuilderItemIndexDateValue({
            id: indexDateId,
            type: indexDateType,
            indexDateValue: undefined,
            indexDateEvent: updatedIndexDateEvent as IndexDateEvent,
          })
        );
      } else {
        dispatch(
          setActiveCohortBuilderItemIndexDateValue({
            id: indexDateId,
            type: indexDateType,
            indexDateValue: undefined,
            indexDateEvent: undefined,
          })
        );
      }
    }
  };

  const onEventDateAttributeChange = (value: string) => {
    if (indexDateType === IndexDateTypeLegacy.EVENT) {
      let updatedIndexDateEvent = undefined;
      if (indexDateEvent) {
        updatedIndexDateEvent = {
          ...indexDateEvent,
          eventDateAttribute: {
            ...indexDateEvent.eventDateAttribute,
            value: value,
          },
        };
      }
      dispatch(
        setActiveCohortBuilderItemIndexDateValue({
          id: indexDateId,
          type: indexDateType,
          indexDateValue: indexDateValue,
          indexDateEvent: updatedIndexDateEvent,
        })
      );
    }
  };

  const onOccurrenceChange = (value: string) => {
    if (indexDateType === IndexDateTypeLegacy.EVENT) {
      let updatedIndexDateEvent = undefined;
      if (indexDateEvent) {
        const isNthOccurrence = value === 'NTH';
        updatedIndexDateEvent = {
          ...indexDateEvent,
          occurrence: {
            ...indexDateEvent.occurrence,
            value: value,
          },
          position: !isNthOccurrence
            ? {
                ...indexDateEvent.position,
                value: '',
              }
            : indexDateEvent.position,
        };
        dispatch(
          setActiveCohortBuilderItemIndexDateValue({
            id: indexDateId,
            type: indexDateType,
            indexDateValue: indexDateValue,
            indexDateEvent: updatedIndexDateEvent as IndexDateEvent,
          })
        );
      }
    }
  };

  const onPositionChange = (value: string) => {
    if (indexDateType === IndexDateTypeLegacy.EVENT) {
      let updatedIndexDateEvent = undefined;
      if (indexDateEvent) {
        updatedIndexDateEvent = {
          ...indexDateEvent,
          position: indexDateEvent.position
            ? {
                ...indexDateEvent.position,
                value: value,
              }
            : {
                id: uuidv4(),
                value: value,
              },
        };
      }
      dispatch(
        setActiveCohortBuilderItemIndexDateValue({
          id: indexDateId,
          type: indexDateType,
          indexDateValue: indexDateValue,
          indexDateEvent: updatedIndexDateEvent,
        })
      );
    }
  };

  const transformIndexDateEventForSave = (indexDateEvent?: IndexDateEvent) => {
    if (indexDateType === IndexDateTypeLegacy.CALENDAR) {
      return undefined;
    }
    if (!indexDateEvent) {
      return undefined;
    }
    if (!indexDateEvent.eventName.value) {
      return undefined;
    }

    const isNthOccurrence = indexDateEvent.occurrence.value === 'NTH';
    const position =
      isNthOccurrence && indexDateEvent.position
        ? {
            ...indexDateEvent.position,
            value: indexDateEvent.position.value || '',
          }
        : undefined;

    return {
      ...indexDateEvent,
      position,
    };
  };
  // close the modal without saving
  const onClickCancelIndexDate = () => {
    dispatch(clearModal());
  };

  // save the index date to the definition and close the modal
  const onClickModalSetIndexDate = (
    emitResearchAssistantUpdate: (cohortDefinition: CohortBuilderDefinitionLegacy) => void
  ) => {
    if ((indexDateEvent || indexDateValue) && indexDateType) {
      // only dispatch if all values are present
      const _nextIndexDate = {
        id: indexDateId,
        type: indexDateType,
        indexDateValue,
        indexDateEvent: transformIndexDateEventForSave(indexDateEvent),
        indexDateRangeValue:
          indexDateRangeStartValue && indexDateRangeEndValue
            ? {
                startDate: indexDateRangeStartValue ? indexDateRangeStartValue.toISOString() : '',
                endDate: indexDateRangeEndValue ? indexDateRangeEndValue.toISOString() : '',
              }
            : undefined,
      };
      dispatch(setIndexDate(_nextIndexDate));

      const { cohortDefinition: nextCohortDefinition } = getFullCohortDslForIndexDate(
        currentCohortDefinition,
        _nextIndexDate
      );
      emitResearchAssistantUpdate(nextCohortDefinition);
    }
  };

  const onStartDateChange = (_id: string, date: Dayjs | null) => {
    setIndexDateRangeStartValue(date);
    // Clear error when date changes
    setHasDateBoundsError(false);
    dispatch(
      setActiveCohortBuilderItemIndexDateValue({
        id: indexDateId,
        type: indexDateType as IndexDateTypeLegacy,
        indexDateValue: indexDateValue || '',
        indexDateEvent: indexDateEvent || undefined,
        indexDateRangeValue: {
          startDate: date?.isValid() ? date.toISOString() : '',
          endDate: indexDateRangeValue?.endDate || '',
        },
      })
    );
  };

  const onEndDateChange = (_id: string, date: Dayjs | null) => {
    setIndexDateRangeEndValue(date);
    // Clear error when date changes
    setHasDateBoundsError(false);
    dispatch(
      setActiveCohortBuilderItemIndexDateValue({
        id: indexDateId,
        type: indexDateType as IndexDateTypeLegacy,
        indexDateValue: indexDateValue || '',
        indexDateEvent: indexDateEvent || undefined,
        indexDateRangeValue: {
          startDate: indexDateRangeValue?.startDate || '',
          endDate: date?.isValid() ? date.toISOString() : '',
        },
      })
    );
  };

  const onClickDateRangeCheckbox = () => {
    if (isDateRangeEnabled) {
      setIndexDateRangeEndValue(null);
      setIndexDateRangeStartValue(null);
      setHasDateBoundsError(false);
    }
    setIsDateRangeEnabled(!isDateRangeEnabled);
  };

  const baseConfigForDatePickers: Pick<
    DatePickerProps,
    'minDate' | 'maxDate' | 'disabled' | 'onError' | 'viewMode' | 'placement'
  > = {
    minDate: dayjs.utc('1900-01-01'),
    maxDate: dayjs.utc('2099-12-31'),
    disabled: !isDateRangeEnabled,
    viewMode: !isEditable,
    placement: 'top-end',
    onError: onDateRangeError,
  };

  const indexStartDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'index-date-start-date-picker',
    value: indexDateRangeStartValue,
    onChange: onStartDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.setIndexDateDialog.startDateLabel',
    }),
  };

  const indexEndDatePicker: DatePickerProps = {
    ...baseConfigForDatePickers,
    id: 'index-date-end-date-picker',
    value: indexDateRangeEndValue,
    onChange: onEndDateChange,
    displayLabel: intl.formatMessage({
      id: 'projects.cohortBuilderPage.setIndexDateDialog.endDateLabel',
    }),
  };

  useEffect(() => {
    // Only set default type for new index dates (when no type is selected yet)
    if (!indexDateType) {
      onSelectTypeChange(indexDateId, IndexDateTypeLegacy.EVENT);
    }
    // To set the index date type to event by default
  }, [indexDateType]);

  return {
    isSaveAllowed,
    indexDateId,
    indexDateType,
    indexDateValue: indexDateValue ?? null,
    indexDateEvent: indexDateEvent ?? null,
    indexDateValueAsDate,
    inputFieldConfigMap,
    onSelectTypeChange,
    onEventChange,
    onEventDateAttributeChange,
    onOccurrenceChange,
    onPositionChange,
    onDatePickerChange,
    onDatePickerError,
    datePickerErrorMsg,
    onClickCancelIndexDate,
    onClickModalSetIndexDate,
    isEditable,
    indexStartDatePicker,
    indexEndDatePicker,
    isDateRangeEnabled,
    onClickDateRangeCheckbox,
  };
};
